import type { Event, Stacktrace } from "@sentry/types";

// Drops an open upstream mapbox-gl-js bug (#13590) where a mouseup on the map
// canvas reaches `vec4.transformMat4` with an undefined `pixelMatrixInverse`
// via `unproject` -> `pointRayIntersection`. Stacktrace is entirely inside
// node_modules/mapbox-gl and there is no first-party mitigation.
// https://github.com/mapbox/mapbox-gl-js/issues/13590
//
// Match strategy: at `beforeSend` time, frame `filename`/`abs_path` is the
// runtime URL of the webpack chunk that bundles mapbox-gl, NOT
// `./node_modules/mapbox-gl/dist/mapbox-gl.js` (that path only exists after
// Sentry applies source maps server-side). So filename matching is unreliable
// here. Instead, we require at least two distinct mapbox-internal frames from
// the unproject -> ray-intersection path to appear in the same stack. These
// names are specific enough to mapbox-gl that a co-occurrence is a strong
// signal, while being more conservative than matching a single generic name
// like `transformMat4` (which is also used by gl-matrix, three.js, etc.).
const MAPBOX_UNPROJECT_FUNCTIONS = [
  "pointRayIntersection",
  "pointLocation3D",
  "pointCoordinate3D",
  "pointCoordinate",
];
const MIN_MATCHING_FRAMES = 2;

const frameFunctionMatchesMapbox = (functionName: string | undefined): boolean => {
  if (!functionName) return false;

  return MAPBOX_UNPROJECT_FUNCTIONS.some(name => functionName.includes(name));
};

const stacktraceIsMapboxUnprojectCrash = (stacktrace?: Stacktrace): boolean => {
  const frames = stacktrace?.frames;
  if (!frames?.length) return false;

  const matchingFrameCount = frames.filter(frame =>
    frameFunctionMatchesMapbox(frame.function)
  ).length;

  return matchingFrameCount >= MIN_MATCHING_FRAMES;
};

const errorIsMapboxGlUnprojectCrash = (event: Event): boolean => {
  return !!event?.exception?.values?.some(exception =>
    stacktraceIsMapboxUnprojectCrash(exception?.stacktrace)
  );
};

const mapboxGlUnproject = (event: Event): Event | null => {
  if (errorIsMapboxGlUnprojectCrash(event)) {
    return null;
  }

  return event;
};

export { mapboxGlUnproject };
